'use client';

/**
 * API namespace `market` — orchestrator + top-level methods.
 * Verbatim extraction from frontend/src/lib/api-client.ts:
 *   - market top-level methods: L7610-L7733 (before orders sub-namespace)
 *   - market header: L7573 (moved verbatim)
 *   - market.orders: imported from ./market-orders
 *   - market.disputes: imported from ./market-disputes
 *   - market.reviews: imported from ./market-reviews
 *   - market.bundles: imported from ./market-bundles
 *   - market.coupons: imported from ./market-coupons
 *   - market.giftCards: imported from ./market-gift-cards
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import { MARKET_CATEGORIES } from '../core/shared-types';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

import { orders as marketOrders } from './market-orders';
import { disputes as marketDisputes } from './market-disputes';
import { reviews as marketReviews } from './market-reviews';
import { bundles as marketBundles } from './market-bundles';
import { coupons as marketCoupons } from './market-coupons';
import { giftCards as marketGiftCards } from './market-gift-cards';

export const market = {
  /**
   * GET /api/market/products — paginated public catalog.
   *
   * Returns `ProductListResponse` (`products: PublicProductResponse[]`,
   * `total`, `page`, `limit`). The backend clamps `page >= 1` and `limit`
   * to `[1, 60]` (defense in depth — the frontend can send any value and
   * the backend will normalize). The backend independently filters by
   * `status='approved'` + applies the search/category/subcategory/type/
   * price/sortBy filters server-side.
   *
   * Authz: PUBLIC (no auth required). 403 `MARKET_PRODUCTS_DISABLED` when
   * the `market_products_enabled` feature flag is off.
   *
   * Spec ref: SPEC-081 Fase 3 §5.1, §4.1 (listProducts).
   */
  async listProducts(
    filters: ProductListFilters,
    signal?: AbortSignal,
  ): Promise<ProductListResponse> {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        qs.set(k, String(v));
      }
    });
    const query = qs.toString();
    return apiFetch<ProductListResponse>(
      query ? `/market/products?${query}` : '/market/products',
      { auth: false, signal },
    );
  },
  /**
   * GET /api/market/products/:storeSlug/:productSlug — public product
   * detail.
   *
   * Returns `PublicProductResponse` — a privacy-filtered view of the
   * product (NO `sellerId`, `adminNotes`, `reviewedBy`, `reviewedAt`,
   * `views` — admin-only fields). Includes a `seller` snapshot
   * (`storeName`, `storeSlug`, `rating`, `fulfillmentOptions`) so the
   * detail page can render shipping/pickup info without an extra
   * `getPublicStore` roundtrip. `digitalConfig.downloadUrl` is OMITTED
   * (INV-MKT-14 — only exposed after purchase via a signed URL in Fase 5).
   *
   * Authz: PUBLIC. 404 if the product does not exist OR is not
   * `status='approved'` (the backend hides pending/rejected/paused/
   * out_of_stock/deleted products — INV-MKT-20). 403
   * `MARKET_PRODUCTS_DISABLED` when the flag is off.
   *
   * Spec ref: SPEC-081 Fase 3 §5.1, §4.1 (getProductBySlug), §6.2
   *           (frontend product detail page).
   */
  async getProductBySlug(
    storeSlug: string,
    productSlug: string,
    signal?: AbortSignal,
  ): Promise<PublicProductResponse> {
    return apiFetch<PublicProductResponse>(
      `/market/products/${encodeURIComponent(storeSlug)}/${encodeURIComponent(productSlug)}`,
      { auth: false, signal },
    );
  },
  /**
   * Returns the canonical 15 categories (SPEC-NEGOCIO §10.11 verbatim).
   *
   * STATIC — does NOT call the backend. The `MARKET_CATEGORIES` constant
   * is imported at the top of this file from `@profileia/shared` (Task A)
   * and is the single source of truth shared by:
   *   - This `api.market.listCategories()` method (frontend catalog +
   *     product form select).
   *   - The Zod schema `productCreateSchema.refine()` (Task A — cross-
   *     field validation).
   *   - The backend `market.service.createProduct` (Task B — defense-in-
   *     depth category check).
   *
   * The method signature is async to mirror the other `api.market.*`
   * methods (callers can `await` it uniformly) + to leave room for a
   * future backend-driven categories endpoint without a breaking change.
   *
   * §6 Anti-Invención: the 15 entries are VERBATIM from SPEC-NEGOCIO
   * §10.11 — DO NOT add, remove, rename, or reorder.
   *
   * Spec ref: SPEC-081 Fase 3 §3.2 (categories constant), §6.7 (API
   *           client surface), SPEC-NEGOCIO §10.11.
   */
  async listCategories() {
    return MARKET_CATEGORIES;
  },
  // ──────────────────────────────────────────────────────────────────────
  // SPEC-081 Fase 4 (Escrow + Compras — Profileia Market — Task C1).
  //
  // `orders` sub-object — typed wrappers for the BUYER order endpoints.
  // All endpoints live under `/api/market/orders/*` (Task B —
  // `order.controller.ts` + `order.routes.ts`) and require `requireAuth`
  // + `requireMarketOrdersEnabled` middleware (router-level). POST
  // `/market/orders` additionally requires `orderCreateLimiter`
  // (10/h per user) + `requireRecent2FA` (TOTP/wallet 2FA within last
  // 5 min — financial-grade op: debits buyer credits to escrow).
  //
  // §6 Anti-Invención: NO invented shapes — request + response types come
  // from `@profileia/shared` (`types/order.ts` — Task A). The Zod schema
  // `orderCreateSchema` (Task A) is the single source of truth for the
  // POST request body; the backend re-uses it via `safeParse`. The
  // frontend does NOT duplicate validation here (defense in depth lives
  // in the backend; the frontend's role is to gather user input + send
  // it).
  //
  // Idempotency (INV-SEC-03): the buyer SHOULD send an
  // `Idempotency-Key` HTTP header on POST `/market/orders` to make the
  // create call idempotent (Redis SETNX, 24h TTL — same key returns the
  // same order without double-debiting). The header is set via the
  // `headers` field on `FetchOptions` (RequestInit.headers) — same
  // pattern as the seller-product create flow (the backend reads
  // `req.headers['idempotency-key']` in the controller). The frontend
  // generates a fresh UUID per purchase attempt via `safeUUID()`.
  //
  // Spec ref: SPEC-081 Fase 4 §5.1 (Buyer order routes), §6.7 (API
  //           client surface), §4.1 (createOrder / confirmReceipt flows),
  //           ADR-039 + enmienda INV-MKT-21 (aprobada por Founder 2026-08-28),
  //           SPEC-NEGOCIO §10.5 (INV-MKT-09 escrow off-chain) + §10.6
  //           (INV-MKT-21 fulfillment method selection).
  // ──────────────────────────────────────────────────────────────────────

  orders: marketOrders,
  disputes: marketDisputes,
  reviews: marketReviews,
  bundles: marketBundles,
  coupons: marketCoupons,
  giftCards: marketGiftCards,
};

